import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

/* Кнопка. Минимальная высота 44px задана в theme.css — палец должен попадать. */
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: 'primary' | 'ghost';
  readonly wide?: boolean;
  readonly children: ReactNode;
}

export function Button({
  variant = 'primary',
  wide = false,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = ['btn', `btn--${variant}`, wide ? 'btn--wide' : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <button className={classes} type={type} {...rest}>
      {children}
    </button>
  );
}

/*
 * Поле ввода.
 *
 * Ошибка связана с полем через aria-describedby, а не просто покрашена:
 * цвет не доходит до screen reader и до игрока с дальтонизмом.
 */
interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly id: string;
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
}

export function TextField({ id, label, error, hint, className, ...rest }: TextFieldProps) {
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        className={['input', error ? 'input--invalid' : '', className ?? '']
          .filter(Boolean)
          .join(' ')}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />
      {error ? (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
      {hint && !error ? (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/*
 * Скелет загрузки.
 *
 * Документы требуют скелет, а не крутилку: игрок видит форму будущего
 * содержимого и понимает, что грузится, а не «что-то происходит».
 */
export function Skeleton({ variant }: { readonly variant: 'title' | 'text' | 'card' }) {
  return <div className={`skeleton skeleton--${variant}`} aria-hidden="true" />;
}

/* Экран ошибки. Повтор только по кнопке: автоповтор POST запрещён. */
interface ErrorStateProps {
  readonly title: string;
  readonly message: string;
  readonly onRetry?: (() => void) | undefined;
}

export function ErrorState({ title, message, onRetry }: ErrorStateProps) {
  return (
    <div className="screen screen--center enter" role="alert">
      <section className="state-panel">
        <h1 className="h1">{title}</h1>
        <p className="body">{message}</p>
        {onRetry ? (
          <Button onClick={onRetry} wide>
            Повторить
          </Button>
        ) : null}
      </section>
    </div>
  );
}
